import { useState, useEffect } from 'react';

/**
 * Interface representing the current authenticated user
 * Provides type-safe access to user information from localStorage
 */
interface CurrentUser {
  /** User's unique identifier, null if not authenticated */
  id: number | null;
  /** User's email address, null if not available */
  email: string | null;
  /** Whether the user is authenticated with a valid ID */
  isAuthenticated: boolean;
}

/**
 * Custom hook to access current user information reactively
 *
 * Provides centralized access to user ID and email from localStorage,
 * with automatic reactivity to login/logout events across browser tabs.
 *
 * @returns CurrentUser object with id, email, and authentication status
 *
 * @example
 * // Basic usage
 * const { id: userId, isAuthenticated } = useCurrentUser();
 *
 * if (!isAuthenticated || userId === null) {
 *   return <LoginPrompt />;
 * }
 *
 * // Access user data
 * const preferences = getPreferences(userId);
 *
 * @example
 * // Full user info
 * const { id, email, isAuthenticated } = useCurrentUser();
 *
 * return (
 *   <div>
 *     {isAuthenticated ? (
 *       <p>Welcome, {email}!</p>
 *     ) : (
 *       <p>Please log in</p>
 *     )}
 *   </div>
 * );
 *
 * @see https://github.com/WordenPond/localartistfairs/issues/1149
 */
export function useCurrentUser(): CurrentUser {
  const [user, setUser] = useState<CurrentUser>(() => {
    // Initialize from localStorage on first render
    const userId = localStorage.getItem('user_id');
    const userEmail = localStorage.getItem('user_email');

    const parsedId = userId ? parseInt(userId, 10) : null;
    const validId = parsedId !== null && !isNaN(parsedId);

    return {
      id: validId ? parsedId : null,
      email: userEmail,
      isAuthenticated: validId,
    };
  });

  useEffect(() => {
    // Listen for storage changes (login/logout in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      // Only react to user_id or user_email changes
      if (e.key === 'user_id' || e.key === 'user_email') {
        const userId = localStorage.getItem('user_id');
        const userEmail = localStorage.getItem('user_email');

        const parsedId = userId ? parseInt(userId, 10) : null;
        const validId = parsedId !== null && !isNaN(parsedId);

        setUser({
          id: validId ? parsedId : null,
          email: userEmail,
          isAuthenticated: validId,
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return user;
}
